import { motion } from "framer-motion";
import { ArrowRight, Handshake } from "lucide-react";
import DeploymentFootprint from "./DeploymentFootprint";
import DeploymentImpactStats from "./DeploymentImpactStats";
import LogoMarquee from "./LogoMarquee";
import PulsingPillIcon from "./PulsingPillIcon";
import { primaryCtaClass } from "../utils/ctaClasses";
import { slowFadeUp } from "../utils/animations";

export default function SocialProof() {
  return (
    <section
      id="resources"
      className="section-viewport scroll-mt-24 bg-slate-50 px-4 py-12 md:px-6 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center md:mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Handshake} tone="blue" />
            Social Proof
          </span>
        </div>

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-lg shadow-slate-900/5">
          <div className="relative isolate min-h-[300px] overflow-hidden px-6 py-10 md:min-h-[360px] md:px-10 md:py-14 lg:px-14">
            <div
              className="pointer-events-none absolute inset-0 -z-20 bg-cover bg-[position:62%_center] bg-no-repeat lg:bg-[position:58%_center]"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/social-proof-hero.png)`,
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-white/95 from-0% via-white/80 via-40% to-white/45 to-100% md:bg-gradient-to-r md:from-white/90 md:from-[-2%] md:via-white/75 md:via-[38%] md:to-white/15 md:to-[78%] lg:via-[36%] lg:to-transparent lg:to-[85%]"
              aria-hidden
            />
            <div className="relative z-10 max-w-xl">
              <span className="sr-only">
                Illustration: modern hospitals and a connected world map
                representing global healthcare deployments.
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-hover">
                Social proof
              </p>
              <h2 className="font-display mt-3 text-section font-bold leading-tight text-slate-900">
                Trusted by Healthcare Providers Across Countries
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
                Our systems have been implemented across hospitals and healthcare
                networks, delivering reliable performance in real-world
                environments.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href="#demo-form"
                  className={`${primaryCtaClass} gap-2 px-6 py-3.5 text-sm font-bold`}
                >
                  Request Demo
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </a>
                <a
                  href="#office"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white/90 px-6 py-3.5 text-sm font-bold text-slate-800 shadow-sm backdrop-blur-[2px] transition hover:border-slate-300 hover:bg-slate-50 md:bg-white/80"
                >
                  Talk to Our Team
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="mt-14 md:mt-20">
          <div className="mb-8 flex items-center gap-4 md:mb-10">
            <span className="h-px flex-1 bg-slate-200" aria-hidden />
            <p className="shrink-0 text-center text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
              Trusted by leading healthcare institutions
            </p>
            <span className="h-px flex-1 bg-slate-200" aria-hidden />
          </div>
          <LogoMarquee />
        </div>

        <DeploymentImpactStats className="mt-14 md:mt-20" />

        {/* Deployment footprint — country list + interactive globe */}
        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          <DeploymentFootprint />
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative isolate mt-12 flex flex-col gap-6 overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/70 to-emerald-50/50 px-6 py-8 shadow-lg shadow-slate-900/5 md:mt-16 md:flex-row md:items-center md:gap-8 md:px-10 md:py-10"
        >
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-400/15 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-teal-400/10 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(16,185,129,0.08),transparent)]"
            aria-hidden
          />

          <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <Handshake className="h-7 w-7" strokeWidth={1.75} aria-hidden />
          </div>
          <div className="relative z-10 min-w-0 flex-1">
            <h3 className="font-display text-xl font-bold text-slate-900 md:text-2xl">
              Let&apos;s build the right system for your hospital
            </h3>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
              Partner with Gestalt to streamline operations, enhance patient care,
              and drive better outcomes.
            </p>
          </div>
          <div className="relative z-10 flex w-full flex-col gap-3 sm:flex-row md:w-auto md:shrink-0">
            <a
              href="#demo-form"
              className={`${primaryCtaClass} gap-2 px-5 py-3 text-sm font-bold`}
            >
              Request Demo
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href="#office"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white/90 px-5 py-3 text-sm font-bold text-slate-800 shadow-sm backdrop-blur-[2px] transition hover:border-slate-300 hover:bg-slate-50"
            >
              Talk to Our Team
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
